import { takesMoney } from "../utils/takesMoney";
import { openMoneyGate } from "../lib/connectBank";
import { freeTierLine } from "../utils/freeTierLine";
import React, { useState } from "react";
import NumberPad from "./NumberPad";
import PriceFloorNote from "./PriceFloorNote";
import { belowFloor, liftToFloor } from "../utils/priceFloor";
import { FieldStatus } from "./FieldStatus";
import { currencySymbol } from "../utils/currency";
import { roundPriceStep } from "../utils/roundPriceStep";
import { PAYIN_CURRENCIES, PAYIN_CURRENCY_FLAGS, PayinCurrency } from "../utils/payinConfig";
import { SUFFIX_CUR } from "../utils/requestsFormat";
import { usersApi } from "../services/users";
import type { User } from "../types/User";
import type { ProfileAutosave } from "../hooks/useProfileAutosave";
import { t as tr, formatLocale } from "../i18n";

// The Request Price glow card on the Profile page: the amount, the currency
// pill, the number pad, and the save that mirrors the Requests page. Split out
// of pages/ProfilePage.tsx; the saved price itself stays on the page because
// the preview draft reads it.

// Thousands separators for a price the card DISPLAYS ("20,000"). The symbol is
// rendered in its own span here, so this groups digits only. High-denomination
// currencies (HUF 200 000, CZK 12 000) are unreadable without it.
const groupDigits = (raw: string) => {
  const n = Number(raw);
  if (raw === "" || !Number.isFinite(n)) return raw;
  return n.toLocaleString(formatLocale(), { maximumFractionDigits: 2 });
};

type Props = {
  user: User | null;
  profile: User;
  currency: PayinCurrency;
  requestPriceInput: string;
  setRequestPriceInput: React.Dispatch<React.SetStateAction<string>>;
  setProfile: React.Dispatch<React.SetStateAction<User | null>>;
  handleCurrencyChange: (next: PayinCurrency) => Promise<void>;
  autosave: ProfileAutosave;
};

export default function RequestPriceCard({ user, profile, currency, requestPriceInput, setRequestPriceInput, setProfile, handleCurrencyChange, autosave }: Props) {
  const { fieldStatus, setSaving, markSaved, setSaveError } = autosave;
  // Tapping the price box opens our shared NumberPad (same keypad as onboarding)
  // instead of the OS keyboard. `priceDraft` is the in-pad buffer; it commits to
  // the saved price on Done. Starts blank each open (Cash App / Venmo style), so
  // tapping Done without typing leaves the existing price untouched.
  const [pricePadOpen, setPricePadOpen] = useState(false);
  const [priceDraft, setPriceDraft] = useState("");
  // "Tap to change what fans pay" reads like a tutorial line — show it only
  // until the first successful price save from this page (per-account, this
  // device), then the card just says "Request Price".
  const [priceHintSeen, setPriceHintSeen] = useState<boolean>(() => {
    try {
      return !!user?.slug && localStorage.getItem(`priceHintSeen:${user.slug}`) === "1";
    } catch {
      return false;
    }
  });
  const markPriceHintSeen = () => {
    setPriceHintSeen(true);
    try {
      if (user?.slug) localStorage.setItem(`priceHintSeen:${user.slug}`, "1");
    } catch { /* private-mode etc. — hint just shows again next visit */ }
  };

  // Price: save on blur/Enter, only when changed. Matches the Requests page.
  const saveRequestPrice = async (rawValue: string) => {
    if (!user || !profile) return;
    const parsed = Number((rawValue || "0").replace(",", "."));
    if (!Number.isFinite(parsed) || parsed < 0) {
      setSaveError("price", tr("requestPriceCard.enterAValidPrice"));
      return;
    }
    const fixed = roundPriceStep(parsed, currency);
    if (fixed === roundPriceStep(Number(profile.pricePerRequest) || 0, currency)) return;
    setSaving("price");
    try {
      await usersApi.setPrice(user.slug, fixed);
      setProfile((p) => (p ? { ...p, pricePerRequest: fixed } : p));
      setRequestPriceInput(String(fixed));
      markSaved("price");
      markPriceHintSeen();
    } catch (e) {
      console.error("Price save failed:", e);
      setSaveError("price", tr("requestPriceCard.couldnTSaveYour"));
    }
  };

  const openPricePad = () => {
    setPriceDraft(""); // start fresh, like Cash App / Venmo
    setPricePadOpen(true);
  };

  const commitPricePad = () => {
    const t = priceDraft.trim();
    if (t !== "") {
      const typed = Number(t.replace(",", "."));
      if (Number.isFinite(typed)) {
        // Under the floor ("Minimum $2" on the pad) → the floor; 0 stays Free.
        const n = liftToFloor(typed, currency, profile);
        // Reflect the rounded value immediately so the box + live preview match
        // what gets saved; saveRequestPrice does the validate/round/PATCH.
        setRequestPriceInput(String(roundPriceStep(n, currency)));
        saveRequestPrice(String(n));
      }
    }
    setPriceDraft("");
    setPricePadOpen(false);
  };

  // FREE TIER: fans of a talent who can't take money yet are asked nothing,
  // so the card says so instead of showing a price nobody is charged. The
  // card keeps its place and its look; the tap opens the next step.
  if (!takesMoney(profile)) {
    const line = freeTierLine(profile);
    return (
      <div className="profile-price-card-wrap">
        <div className="profile-price-card">
          <button
            type="button"
            className="profile-price-card-hit"
            onClick={() => openMoneyGate(profile)}
            aria-label={tr("requestPriceCard.requestPriceFree", { line: line || "Connect your bank to receive paid requests" })}
          >
            <div className="profile-price-card-label">{tr("requestPriceCard.requestPrice")}</div>
            <div className="profile-price-card-amount">
              <span className="profile-price-card-value">{tr("requestPriceCard.free")}</span>
            </div>
            {line && <div className="profile-price-card-hint">{line}</div>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Request Price — one compact glow card (price left, label right,
          currency pill on the far right). The card is a div and the price
          area its own button: a <select> can't nest inside a <button>. The
          "Tap to change…" hint shows only until the first price save
          (priceHintSeen). The old .profile-request-price-* classes still
          dress the setlist-price box inside its drawer — don't reuse them
          here. */}
      <div className="profile-price-card-wrap">
        <div className={`profile-price-card${pricePadOpen ? " editing" : ""}`}>
          <button
            type="button"
            className="profile-price-card-hit"
            onClick={openPricePad}
            aria-label={tr("requestPriceCard.editRequestPrice")}
          >
            {/* Label ABOVE the amount (founder decision): beside it, the
                label + currency pill crushed 3-digit prices — stacked, the
                number gets the full width in every currency. */}
            <div className="profile-price-card-label">{tr("requestPriceCard.requestPrice")}</div>
            <div className="profile-price-card-amount">
              {!SUFFIX_CUR.has(currency) && (
                <span className="profile-price-card-cur" aria-hidden>
                  {currencySymbol(currency)}
                </span>
              )}
              <span className="profile-price-card-value">
                {/* Grouped only when idle — while the pad is open the raw draft
                    IS the edit buffer, and commas in it would fight typing. */}
                {pricePadOpen
                  ? priceDraft || "0"
                  : groupDigits(requestPriceInput) || "0"}
              </span>
              {SUFFIX_CUR.has(currency) && (
                <span className="profile-price-card-cur" aria-hidden>
                  {" "}{currencySymbol(currency)}
                </span>
              )}
            </div>
            {!priceHintSeen && (
              <div className="profile-price-card-hint">{tr("requestPriceCard.tapToChangeWhat")}</div>
            )}
          </button>
          {/* Same save path as the Edit-profile drawer's currency dropdown. */}
          <select
            className="profile-price-cur-select"
            value={currency}
            onChange={(e) => handleCurrencyChange(e.target.value as PayinCurrency)}
            aria-label={tr("requestPriceCard.changeCurrency")}
          >
            {PAYIN_CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {PAYIN_CURRENCY_FLAGS[c]} {c.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
        <FieldStatus status={fieldStatus.price} />
        <FieldStatus status={fieldStatus.currency} />
        <NumberPad
          open={pricePadOpen}
          value={priceDraft}
          onChange={setPriceDraft}
          onDone={commitPricePad}
          allowDecimal
          withBackdrop
          maxLength={6}
          note={<PriceFloorNote currency={currency} talent={profile} below={belowFloor(priceDraft, currency, profile)} allowFree />}
        />
      </div>
    </>
  );
}
